import { useEffect, useRef } from "react";

export const useOutsideClick = (
  callback: (event: MouseEvent) => void,
  /* A screen can hold dozens of these; a closed one should not be listening. */
  enabled = true,
) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      // A click on something React has since removed (a search result that
      // was picked, a chip that was dismissed) has no ancestors left to check
      // against, and it happened inside the app, not outside it.
      if (target && !target.isConnected) return;
      // Portalled layers (Popover panels, …) sit outside the ref's subtree in
      // the DOM but are logically inside it — clicking them is not "outside".
      if (
        target instanceof Element &&
        target.closest("[data-outside-click-ignore]")
      ) {
        return;
      }
      if (ref.current && !ref.current.contains(target)) {
        callback(event);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [callback, enabled]);

  return ref;
};
